import { useState } from 'react';
import WhiteLabelPaymentForm from './WhiteLabelPaymentForm';
import PaddleWhiteLabelPaymentForm from './PaddleWhiteLabelPaymentForm';
import whiteLabelService from '../../services/whiteLabelService';
import { config } from '../../config';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import { usePaymentProvider } from '../../hooks/usePaymentProvider';
import '../../pages/scss/BillingTable.scss'; // reuse the exact plan-card UI
import './scss/WhiteLabelBilling.scss';
import CircleCheckOutlined from '../icons/CircleCheckOutlined';

/**
 * Render the Stripe or Paddle white-label payment form based on the active
 * provider, so both render sites below stay provider-agnostic.
 */
const WhiteLabelPaymentFormForProvider = ({ onSuccess, onError }) => {
  const { isPaddle } = usePaymentProvider();
  return isPaddle
    ? <PaddleWhiteLabelPaymentForm onSuccess={onSuccess} onError={onError} />
    : <WhiteLabelPaymentForm onSuccess={onSuccess} onError={onError} />;
};

// "What's included" list inside each plan card — mirrors the billing page's
// .feat-head + .feats markup so it inherits that styling.
const WlFeatureList = ({ features }) => (
  <>
    <div className="feat-head">What’s included</div>
    <ul className="feats">
      {features.map((f) => (
        <li key={f}>
          <span className="chk"><CircleCheckOutlined size={14} color="var(--brand-color)" /></span>
          {f}
        </li>
      ))}
    </ul>
  </>
);

const WhiteLabelBilling = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const workspaces = useSelector((s) => s.workspaces?.workspaces);
  const { isPaddle } = usePaymentProvider();

  // Premium is a workspace-level plan, so route Premium upgrade to a workspace's
  // billing page. Prefer one the user owns; fall back to the first workspace.
  const goToPremiumUpgrade = () => {
    const list = Array.isArray(workspaces) ? workspaces : [];
    const target = list.find((w) => w.shortId) || null;
    if (target) navigate(`/w/${target.shortId}/billing`);
    else navigate('/');
  };
  // Lifetime WL is the top lifetime tier, bought on the workspace billing page's
  // Lifetime tab (one-time purchase via our own payment system).
  const goToLifetime = () => {
    const list = Array.isArray(workspaces) ? workspaces : [];
    const target = list.find((w) => w.shortId) || null;
    navigate(target ? `/w/${target.shortId}/billing?tab=lifetime` : '/');
  };
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [error, setError] = useState('');
  const [canceling, setCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  // Optimistic local trial state to avoid stale UI after starting trial
  const [optimisticTrial, setOptimisticTrial] = useState(null);

  const paymentsDisabled = config.DISABLE_WHITE_LABEL_PAYMENTS;
  const { onPurchaseSuccess, billingInfo = null, hasActiveSubscription = false, onCancelSuccess, usageInfo = null, trialInfo = null, onTrialStart } = props;


  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePaymentSuccess = () => {
    setProcessing(true);
    setShowPaymentForm(false);

    // White label addon purchased successfully! Setting up your instance...

    if (onPurchaseSuccess) {
      onPurchaseSuccess();
    }

    // Keep processing state for a bit to show loading
    setTimeout(() => {
      setProcessing(false);
    }, 3000);
  };

  const handlePaymentError = (error) => {
    setError(error);
  };

  const handleStartTrial = async () => {
    try {
      setStartingTrial(true);
      setError('');

      const response = await whiteLabelService.startTrial();

      if (response.success) {
        showSuccess(dispatch, 'White label trial started successfully! You have 90 days to explore all features.');

        // Optimistically reflect trial in UI immediately to avoid stale props
        const now = new Date();
        const end = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
        setOptimisticTrial({
          isTrialing: true,
          trialStartDate: response?.trial?.trialStartDate || now.toISOString(),
          trialEndDate: response?.trial?.trialEndDate || end.toISOString(),
          trialUsed: true,
          daysRemaining: response?.trial?.daysRemaining ?? 90
        });

        // Wait a bit for backend to fully process
        await new Promise(resolve => setTimeout(resolve, 500));

        if (onTrialStart) {
          await onTrialStart();
        }

        // Reset loading state after refresh completes
        setStartingTrial(false);
      } else {
        throw new Error(response.message || 'Failed to start trial');
      }
    } catch (error) {
      console.error('Error starting trial:', error);
      const errorMessage = error.message || 'Failed to start trial. Please try again.';
      setError(errorMessage);
      showError(dispatch, errorMessage);
      setStartingTrial(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCanceling(true);
      setError('');

      const response = await whiteLabelService.cancelSubscription();

      if (response.success) {
        setShowCancelConfirm(false);
        showSuccess(dispatch, 'White label subscription canceled successfully');
        if (onCancelSuccess) {
          onCancelSuccess();
        }
      } else {
        throw new Error(response.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      const errorMessage = error.message || 'Failed to cancel subscription. Please try again or contact support.';
      setError(errorMessage);
      showError(dispatch, errorMessage);
    } finally {
      setCanceling(false);
    }
  };

  const isTrialing = optimisticTrial?.isTrialing || trialInfo?.isTrialing;
  const isEntitled = hasActiveSubscription || !!optimisticTrial;
  const features = [
    'Custom branding and logo',
    'Custom domain (app.yourbrand.com)',
    'Remove Zoobbe branding',
    'Public signup control & custom support email',
  ];

  // ---- PAYMENT FORM (shared across states) ----
  if (showPaymentForm && !paymentsDisabled) {
    return (
      <div className="wl-billing">
        <div className="wl-panel">
          <button type="button" className="wl-back" onClick={() => setShowPaymentForm(false)}>← Back</button>
          <WhiteLabelPaymentFormForProvider onSuccess={handlePaymentSuccess} onError={handlePaymentError} />
        </div>
      </div>
    );
  }

  // ---- PROCESSING / TRIAL SETUP ----
  if (processing || startingTrial) {
    return (
      <div className="wl-billing">
        <div className="wl-panel wl-center">
          <div className="wl-spinner" />
          <h3>{processing ? 'Setting up White Label…' : 'Starting your trial…'}</h3>
          <p>This will just take a moment — don’t close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wl-billing">
      {/* ============ ACTIVE (subscribed / trialing / included) ============ */}
      {isEntitled && (
        <div className="wl-panel">
          {error && (
            <div className="wl-error" role="alert">
              <span className="material-symbols-outlined">error</span>
              <span>{error}</span>
            </div>
          )}
          <div className="wl-active">
            <div className="wl-active-head">
              <span className="wl-status-dot" />
              <div>
                <h3>{isTrialing ? 'White Label trial active' : 'White Label is active'}</h3>
                <p>{isTrialing
                  ? `${optimisticTrial?.daysRemaining ?? trialInfo?.daysRemaining} days remaining in your trial`
                  : billingInfo?.source === 'premium'
                    ? 'Included with your Premium plan'
                    : (billingInfo?.billingCycle === 'lifetime' || billingInfo?.source === 'lifetime')
                      ? 'Lifetime access — yours for good'
                      : 'Your branded workspace is live'}</p>
              </div>
            </div>

            <div className="wl-rows">
              {isTrialing ? (
                <>
                  <div className="wl-row"><span>Trial started</span><span>{formatDate(optimisticTrial?.trialStartDate || trialInfo?.trialStartDate)}</span></div>
                  <div className="wl-row"><span>Trial ends</span><span>{formatDate(optimisticTrial?.trialEndDate || trialInfo?.trialEndDate)}</span></div>
                </>
              ) : billingInfo && (
                <>
                  <div className="wl-row">
                    <span>Billing</span>
                    <span>{billingInfo?.billingCycle === 'lifetime'
                      ? 'Lifetime · one-time'
                      : billingInfo?.source === 'premium'
                        ? 'Included with Premium'
                        : `$${billingInfo?.price || (billingInfo?.billingCycle === 'yearly' ? '95.99' : '9.99')}/${billingInfo?.billingCycle === 'yearly' ? 'yr' : 'mo'}`}</span>
                  </div>
                  {billingInfo.nextBillingDate && billingInfo?.billingCycle !== 'lifetime' && billingInfo?.source !== 'premium' && (
                    <div className="wl-row"><span>Next billing</span><span>{formatDate(billingInfo.nextBillingDate)}</span></div>
                  )}
                </>
              )}
            </div>

            {/* Action row varies by how they hold White Label */}
            {isTrialing ? (
              <button className="wl-btn wl-btn--primary" onClick={() => setShowPaymentForm(true)}>Upgrade to a paid plan</button>
            ) : (billingInfo?.source === 'premium') ? (
              <p className="wl-note">White Label is part of Premium — it stays active as long as your Premium plan is.</p>
            ) : (billingInfo?.billingCycle === 'lifetime' || billingInfo?.source === 'lifetime') ? (
              <p className="wl-note">Lifetime access — nothing to manage, it’s yours for good.</p>
            ) : !showCancelConfirm ? (
              <button className="wl-btn wl-btn--danger-outline" onClick={() => setShowCancelConfirm(true)}>Cancel add-on</button>
            ) : (
              <div className="wl-cancel-confirm">
                <p>Cancel White Label? This deactivates your branded instance and removes all custom branding. This can’t be undone.</p>
                <div className="wl-cancel-actions">
                  <button className="wl-btn wl-btn--ghost" onClick={() => setShowCancelConfirm(false)} disabled={canceling}>Keep it</button>
                  <button className="wl-btn wl-btn--danger" onClick={handleCancelSubscription} disabled={canceling}>{canceling ? 'Canceling…' : 'Yes, cancel'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ NOT ENTITLED — comparison table (full width) ============ */}
      {!isEntitled && (
        paymentsDisabled ? (
          <div className="wl-panel wl-disabled">
            <span className="material-symbols-outlined">info</span>
            <p>White Label payments are temporarily disabled. Please contact support.</p>
          </div>
        ) : (
          // Connected hairline plan cards — same structure/classes as the
          // billing page's .billing-cards so it inherits that exact UI.
          <div className="billing-container wl-plans">
            <div className="wl-plans-head">
              <h3>Put your own brand on Zoobbe</h3>
              <p>Custom logo, your own domain, and zero Zoobbe branding — three ways to unlock it.</p>
            </div>

            <div className="billing-cards lifetime">
              {/* Premium — included */}
              <div className="card pick">
                <div className="plan__name">Premium <span className="tag">Best value</span></div>
                <div className="plan__price">
                  <span className="amt">Included</span>
                </div>
                <div className="plan__sub">with your plan</div>
                <div className="plan__desc">White Label is bundled into Premium — no separate charge. Upgrade your workspace and it’s on.</div>
                <button type="button" className="plan__cta" onClick={goToPremiumUpgrade}>Upgrade to Premium</button>
                <WlFeatureList features={features} />
              </div>

              {/* Add-on — paid */}
              <div className="card">
                <div className="plan__name">Add-on</div>
                <div className="plan__price">
                  <span className="cur">$</span><span className="amt">9.99</span><span className="per">/mo</span>
                </div>
                <div className="plan__sub">or $95.99/year</div>
                <div className="plan__desc">On Free or Standard? Add White Label separately, billed monthly or yearly — independent of your plan.</div>
                <button type="button" className="plan__cta plan__cta--solid" onClick={() => setShowPaymentForm(true)}>Buy add-on</button>
                <WlFeatureList features={features} />
              </div>

              {/* Lifetime — one-time purchase (top tier includes White Label) */}
              <div className="card">
                <div className="plan__name">Lifetime <span className="tag">One-time</span></div>
                <div className="plan__price">
                  <span className="amt">Own it</span>
                </div>
                <div className="plan__sub">Pay once, keep forever</div>
                <div className="plan__desc">Buy the top lifetime tier — it includes White Label plus extra seats and storage. One payment, no renewals.</div>
                <button type="button" className="plan__cta" onClick={goToLifetime}>View lifetime deal</button>
                <WlFeatureList features={features} />
              </div>
            </div>

            <div className="assure">
              <div><span className="n">Secure</span><span>Payments &amp; tax handled by Paddle</span></div>
              <div><span className="n">Flexible</span><span>Cancel anytime, no lock-in</span></div>
              <div><span className="n">Yours</span><span>Your brand, your domain</span></div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default WhiteLabelBilling;
